import { useRef, useState } from 'react'

import type { LANG } from '@eshop/type'
import type Eshop from '@eshop/type/eshop'

import i18n from './i18n'
import css from './index.module.css'

import { Input } from '#/global/component/Form/Input'

import { addToCart as addToCartDataLayer } from '#/global/helper/DataLayer/modalCrossSelling'
import { ProductHelper } from '#/global/helper/Product/ProductHelper'

export interface AddToCartEshopAfterParams extends AddToCartEshopParams {
    isSuccess: boolean
    product: Eshop.Product<'detail' | 'price' | 'brand' | 'availability' | 'unit' | 'related' | 'season'>

}

export type AddToCartEshopEventInvoker = 'detail' | 'long' | 'compact' | 'slide'

export interface AddToCartEshopParams {
    code: string
    eventInvoker: AddToCartEshopEventInvoker
    quantitySales: number
    unitSales: string
}

interface Props {
    lang: LANG
    product: Eshop.Product<'price' | 'unit' | 'availability'>
    type: AddToCartEshopEventInvoker
}

export const AddToCartEshop = (props: Props) => {
    const { type } = props
    if (type === 'compact') {
        return <Compact {...props} />
    }
    if (type === 'slide') {
        return <Slide {...props} />
    }
    if (type === 'long') {
        return <Long {...props} />
    }
    if (type === 'detail') {
        // return <Detail {...props} />
    }
}

const Long = ({ lang, product }: Props) => {
    const { availability, code: productCode, price, unit } = product

    const {
        addToCartEshop,
        calculateQuantityPriceEshop: calcPrice,
        calculateQuantityUnitEshopToSales: calcQuantity,
        formatPrice: format,
        formatUnit,
        getAvailabilityStatusByBranchCode,
    } = ProductHelper

    const t = i18n[lang]
    const referenceButton = useRef<HTMLButtonElement>(null)

    const [totalQuantity, setTotalQuantity] = useState({
        unitPrimary: unit.minimumCountUnitPrimary,
        unitSecondary: unit.unitsInPackage,
        unitSales: unit.minimumCountUnitSales,
    })

    const [totalPrice, setTotalPrice] = useState({
        priceVatPrimary: price.priceVatPrimary,
    })

    const handleChange = (q: number) => {
        q = Math.max(q, unit.minimumCountUnitPrimary)

        const totalQuantity = calcQuantity(q, unit)
        const totalPrice = calcPrice(q, price)

        setTotalQuantity({
            unitPrimary: totalQuantity.unitPrimary,
            unitSecondary: totalQuantity.unitSecondary,
            unitSales: totalQuantity.unitSales,
        })

        setTotalPrice({
            priceVatPrimary: totalPrice.priceVatPrimary,
        })
    }

    const addToCart = () => {
        addToCartEshop(productCode, totalQuantity.unitSales, unit.unitSales, 'long')
        referenceButton.current?.blur()
    }

    const availabilityStatus = getAvailabilityStatusByBranchCode(availability, t.demand, t.toCart)

    if (!availabilityStatus.showButton) {
        return (
            <div className={`r-add-to-cart-eshop-long ${css.container}`}></div>
        )
    }

    return (
        <div className={`r-add-to-cart-eshop-long ${css.container}`}>
            <div className={css.inputs}>
                <div className={css.quantity}>
                    <Input
                        autoComplete="off"
                        min="1"
                        onChange={handleChange}
                        type="number"
                        value={totalQuantity.unitPrimary}
                    />
                    <label>{formatUnit(unit.unitPrimary)}</label>
                </div>
                <button className={`dek-button-${availabilityStatus.buttonColor} ${css.button}`} onClick={addToCart} ref={referenceButton} role="button" type="button">
                    {availabilityStatus.buttonText}
                </button>
            </div>
            {unit.isPackagePrimary && <div className={css.row}>{t.labelAddToCart(totalQuantity.unitSecondary, unit.unitSecondary)}</div>}
            <div className={css.row}>
                <strong>{format(totalPrice.priceVatPrimary, price.currencyCode)}</strong>
                {' '}
                {t.total}
                {' '}
                {t.withDPH}
            </div>
        </div>
    )
}

const Compact = ({ lang, product }: Props) => {
    // @keep-sorted
    const { code: productCode, unit } = product

    // @keep-sorted
    const {
        addToCartEshop,
        calculateQuantityPriceEshop: calcPrice,
        calculateQuantityUnitEshopToSales: calcQuantity,
        formatPrice,
    } = ProductHelper

    const t = i18n[lang]

    const [totalQuantity, setTotalQuantity] = useState({
        unitPrimary: unit.minimumCountUnitPrimary,
        unitSecondary: unit.unitsInPackage,
        unitSales: unit.minimumCountUnitSales,
    })

    const [isAlreadyInCart, setIsAlreadyInCart] = useState(false)
    const [isAddingInProcess, setIsAddingInProcess] = useState(false)
    const [price, setPrice] = useState(product.price.priceVatPrimary)

    const handleChange = (q: number) => {
        q = Math.max(q, unit.minimumCountUnitPrimary)

        const totalQuantity = calcQuantity(q, unit)
        const totalPrice = calcPrice(q, product.price)

        setTotalQuantity({
            unitPrimary: totalQuantity.unitPrimary,
            unitSecondary: totalQuantity.unitSecondary,
            unitSales: totalQuantity.unitSales,
        })

        setPrice(totalPrice.priceVatPrimary)
    }

    const addToCart = () => {
        setIsAddingInProcess(true)
        window.addEventListener(`ES_ADD_TO_CART_ESHOP_AFTER`, onAfterAddToCart)
        addToCartEshop(productCode, totalQuantity.unitSales, unit.unitSales, 'compact')
    }

    const onAfterAddToCart = (event: CustomEvent<AddToCartEshopAfterParams>) => {
        if (product.code !== event.detail.code) {
            return
        }
        setIsAddingInProcess(false)
        setIsAlreadyInCart(event.detail.isSuccess)
        event.detail.isSuccess && addToCartDataLayer(productCode)
        window.removeEventListener(`ES_ADD_TO_CART_ESHOP_AFTER`, onAfterAddToCart)
    }

    return (
        <div className={`r-add-to-cart-eshop-compact ${css.compact}`}>
            <div className={css.price}>
                <strong>{formatPrice(price, product.price.currencyCode)}</strong>
                <div>
                    {t.unit(product.unit.unitPrimary)}
                    {' '}
                    {t.withDPH}
                </div>
            </div>

            <div className={css.addToCart}>
                {isAlreadyInCart
                    ? (
                            <div className={css.alreadyInCart}>
                                <img src="https://cdn1.idek.cz/file/check-7c33cfe0.svg" width={16} height={16} />
                                <span>{t.addedToCart}</span>
                            </div>
                        )
                    : (
                            <>
                                <div className={css.input}>
                                    <Input
                                        defaultValue={totalQuantity.unitPrimary}
                                        disabled={isAddingInProcess}
                                        min={1}
                                        onChange={handleChange}
                                        type="number"
                                    />
                                    {product.unit.unitPrimary}
                                </div>
                                <button
                                    className="dek-button-green"
                                    disabled={isAddingInProcess}
                                    onClick={addToCart}
                                    role="button"
                                    type="button">
                                    {isAddingInProcess && <span className={css.loading} />}
                                    {t.addToCart}
                                </button>
                            </>
                        )}
            </div>
        </div>
    )
}

const Slide = ({ lang, product }: Props) => {
    const t = i18n[lang]

    const addToCart = () => {
        const quantitySales = product.unit.isPackagePrimary
            ? product.unit.minimumCountUnitSales
            : product.unit.minimumCountUnitPrimary

        ProductHelper.addToCartEshop(product.code, quantitySales, product.unit.unitSales, 'slide')
    }

    return (
        <div className={`r-add-to-cart-eshop-slide ${css.slide}`}>
            <button
                className={`dek-button-green dek-button-small ${css.button}`}
                onClick={addToCart}
                role="button"
                type="button">
                {t.toCart}
            </button>
        </div>
    )
}
